import React from "react";
import { SidebarTrigger } from "@/components/ui/MultiSidebar";
import { useMatches, UIMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  BreadcrumbLink,
  useBreadcrumb,
} from "@/components/ui/Breadcrumb";
import { MessageCircle, Bell } from "lucide-react";
import { RouteHandle } from "@/routes";
import { useReactiveVar } from "@apollo/client";
import {
  Status as ChatStatus,
  statusVar as chatStatusVar,
} from "@/reactiveVars/chat";
import { useUnreadChatMessagesCount } from "@zeemee/react-web-chat";
import { ProtectedNavLink } from "./ProtectedNavigationLink";
import { StudentSearch } from "./StudentSearch";

export const CrumbsList = ({
  routeMatches,
}: {
  routeMatches: UIMatch<undefined, RouteHandle | undefined>[];
}) => {
  const { dynamicTitle } = useBreadcrumb();

  // If we have a dynamic title, override the last breadcrumb
  const processedMatches =
    dynamicTitle && routeMatches.length > 0
      ? (() => {
          const lastRoute = routeMatches[routeMatches.length - 1];
          return [
            ...routeMatches.slice(0, -1),
            {
              ...lastRoute,
              handle: lastRoute.handle
                ? {
                    ...lastRoute.handle,
                    name: dynamicTitle,
                  }
                : { name: dynamicTitle },
            },
          ];
        })()
      : routeMatches;

  const lastMatch =
    processedMatches.length > 0
      ? processedMatches[processedMatches.length - 1]
      : undefined;

  return (
    <BreadcrumbList data-testid="breadcrumbs-list">
      <BreadcrumbEllipsis className="sm:hidden" />
      <BreadcrumbSeparator className="sm:hidden" />
      {processedMatches.slice(0, -1).map((match) => (
        <React.Fragment key={match.id}>
          <BreadcrumbItem className="hidden sm:flex">
            {(() => {
              if (match.handle?.prefixOnly) {
                return match.handle.name;
              } else {
                return (
                  <BreadcrumbLink
                    className="text-foreground"
                    routeId={match.handle?.routeId}
                    params={
                      Object.fromEntries(
                        Object.entries(match.params || {}).filter(
                          ([, value]) => value !== undefined,
                        ),
                      ) as Record<string, string>
                    }
                    to={match.pathname}
                  >
                    {match.handle?.name}
                  </BreadcrumbLink>
                );
              }
            })()}
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden sm:list-item" />
        </React.Fragment>
      ))}
      {lastMatch && (
        <BreadcrumbItem key={lastMatch.id}>
          {(() => {
            if (lastMatch.handle?.prefixOnly) {
              return lastMatch.handle?.name;
            }
            return (
              <BreadcrumbLink
                className="text-foreground"
                routeId={lastMatch.handle?.routeId}
                params={
                  Object.fromEntries(
                    Object.entries(lastMatch.params || {}).filter(
                      ([, value]) => value !== undefined,
                    ),
                  ) as Record<string, string>
                }
                to={lastMatch.pathname}
                aria-current="page"
              >
                {lastMatch.handle?.name}
              </BreadcrumbLink>
            );
          })()}
        </BreadcrumbItem>
      )}
    </BreadcrumbList>
  );
};

export const Breadcrumbs = () => {
  const { t } = useTranslation();
  const routeMatches = useMatches() as UIMatch<
    undefined,
    RouteHandle | undefined
  >[];
  const matchesWithHandles = routeMatches.filter((match) => match.handle);
  const chatStatus = useReactiveVar(chatStatusVar);
  const { unreadMessagesCount } = useUnreadChatMessagesCount({
    skip: chatStatus !== ChatStatus.BOOTSTRAP_SUCCESS,
  });

  const unreadAlertsCount = 0; // Hard coded to zero for now

  return (
    <div className="flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
      <SidebarTrigger aria-label={t("nav.toggleMenu")} sidebarId="nav" />
      {matchesWithHandles.length > 1 && (
        <>
          <div className="h-4 w-3 border-l"></div>
          <Breadcrumb>
            <CrumbsList routeMatches={matchesWithHandles} />
          </Breadcrumb>
        </>
      )}
      <div className="relative mr-[13px] ml-auto flex items-center gap-4">
        <StudentSearch />
        <div className="relative flex items-center">
          {unreadMessagesCount > 0 && (
            <div className="absolute top-0.5 left-4.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-xs text-white">
              {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
            </div>
          )}
          <ProtectedNavLink
            route="chat"
            aria-label={t("nav.chat")}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 transition-colors hover:bg-slate-200"
          >
            <MessageCircle className="h-5 w-5" />
          </ProtectedNavLink>
        </div>
        {/* Hide for now */}
        <div className="relative flex hidden items-center">
          {unreadAlertsCount > 0 && (
            <div className="absolute top-0.5 left-4.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-xs text-white">
              {unreadAlertsCount > 99 ? "99+" : unreadAlertsCount}
            </div>
          )}
          <ProtectedNavLink
            route="nowhere"
            aria-label={t("nav.alerts", "Alerts")}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 transition-colors hover:bg-slate-200"
          >
            <Bell className="h-5 w-5" />
          </ProtectedNavLink>
        </div>
      </div>
    </div>
  );
};
